import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import {
  treeMarkColorSchema,
  treeMarkShapeSchema,
} from '../enums/tree-mark.enum.js'

/**
 * A species this business surveys, and the mark it gets on a map.
 *
 * Configured in the app rather than compiled in: the list is a property of the
 * business, not of the software, and a crew working a new region meets trees
 * nobody wrote into a schema.
 *
 * There is no DTO for "not identified". That is the absence of a species —
 * `TreeDto.speciesId` is null — and it renders from `UNIDENTIFIED_MARK` and
 * `UNIDENTIFIED_LABEL` in `tree-mark.enum.ts`.
 */
export const treeSpeciesDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  shape: treeMarkShapeSchema,
  color: treeMarkColorSchema,
  sortOrder: z.number().int(),
  archivedAt: z.string().datetime().nullable(),
  /**
   * How many live trees carry this species.
   *
   * Included so the settings screen can say what archiving one would affect —
   * "used by 41 trees" is the difference between an informed decision and a
   * guess — without every caller running its own count.
   */
  treeCount: z.number().int().nonnegative(),
})

export type TreeSpeciesDto = z.infer<typeof treeSpeciesDtoSchema>

export const treeSpeciesListDtoSchema = z.object({
  species: z.array(treeSpeciesDtoSchema),
})

export type TreeSpeciesListDto = z.infer<typeof treeSpeciesListDtoSchema>

/**
 * A species as a map or a document needs to DRAW it.
 *
 * Name and mark, with no id. Used where a species has to survive being detached
 * from its row: a frozen proposal renders years later, and a species that has
 * since been renamed, re-colored or archived must not change what a client was
 * sent. Carrying the values rather than a reference is what makes that true.
 */
export const treeSpeciesMarkSchema = z.object({
  name: z.string(),
  shape: treeMarkShapeSchema,
  color: treeMarkColorSchema,
})

export type TreeSpeciesMark = z.infer<typeof treeSpeciesMarkSchema>
